// pos_local WebSocket klienti — `branch:<id>` kanalına abunə olub domain
// event-lərini real-time qəbul edir (başqa cihazdan/cloud-dan gələn dəyişikliklər).
//
// Protokol (bax: rezmo_pos_local/docs API_CONTRACT.md §WebSocket):
//   → { v:1, t:'subscribe', channel:'branch:<id>', since?:<lastEventId>, token?:<bearer> }
//   ← { v:1, t:'ack', op:'subscribe', channel }
//   ← { v:1, t:'event', channel, envelope:{ eventId, type, payload }, replay? }
//   ← { v:1, t:'pong' } / { v:1, t:'error', message }
//   → { v:1, t:'ping' }  (hər 10s — connection sağlamlığı)
//
// `since` ilə reconnect zamanı qaçırılmış event-lər replay olunur (lastEventId).

import { POS_WS, BRANCH_ID } from './config';
import { getPosToken } from './posClient';

export interface PosEnvelope {
  eventId: string;
  type: string;
  payload?: unknown;
}

export type PosWsStatus = 'connected' | 'disconnected';
type EventCb = (e: PosEnvelope) => void;
type StatusCb = (s: PosWsStatus) => void;

const PING_MS = 10_000;
const RECONNECT_MS = 2_000;

class PosWsClient {
  private ws: WebSocket | null = null;
  private eventCbs = new Set<EventCb>();
  private statusCbs = new Set<StatusCb>();
  private lastEventId: string | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refs = 0;
  private stopped = true;

  /** İstifadəçi (echo wrapper) abunə olur; ref sayğacı 0→1 olanda WS açılır. */
  acquire(): () => void {
    this.refs += 1;
    if (this.refs === 1) this.start();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.refs -= 1;
      if (this.refs <= 0) this.stop();
    };
  }

  onEvent(cb: EventCb): () => void {
    this.eventCbs.add(cb);
    return () => this.eventCbs.delete(cb);
  }

  onStatus(cb: StatusCb): () => void {
    this.statusCbs.add(cb);
    return () => this.statusCbs.delete(cb);
  }

  /** Hazırda bağlıdırmı? (fallback polling-i tənzimləmək üçün.) */
  isConnected(): boolean {
    return this.ws?.readyState === WS_OPEN;
  }

  private start(): void {
    this.stopped = false;
    this.connect();
  }

  private stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.onclose = null;
        this.ws.close();
      } catch {
        /* noop */
      }
      this.ws = null;
    }
  }

  private connect(): void {
    if (typeof WebSocket === 'undefined') return; // SSR/test mühitində WS yoxdur
    let ws: WebSocket;
    try {
      ws = new WebSocket(POS_WS);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      const token = getPosToken();
      ws.send(
        JSON.stringify({
          v: 1,
          t: 'subscribe',
          channel: `branch:${BRANCH_ID}`,
          ...(this.lastEventId ? { since: this.lastEventId } : {}),
          ...(token ? { token } : {}),
        }),
      );
      this.pingTimer = setInterval(() => {
        if (ws.readyState === WS_OPEN) {
          ws.send(JSON.stringify({ v: 1, t: 'ping' }));
        }
      }, PING_MS);
      this.emitStatus('connected');
    };

    ws.onmessage = (e: MessageEvent) => {
      let msg: { t?: string; envelope?: PosEnvelope };
      try {
        msg = JSON.parse(typeof e.data === 'string' ? e.data : '');
      } catch {
        return;
      }
      if (msg.t === 'event' && msg.envelope) {
        this.lastEventId = msg.envelope.eventId || this.lastEventId;
        for (const cb of this.eventCbs) cb(msg.envelope);
      }
    };

    ws.onclose = () => {
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      this.ws = null;
      this.emitStatus('disconnected');
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose ardınca gəlir — reconnect orada idarə olunur.
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.connect();
    }, RECONNECT_MS);
  }

  private emitStatus(s: PosWsStatus): void {
    for (const cb of this.statusCbs) cb(s);
  }
}

// `WebSocket.OPEN` test mühitində olmaya bilər — sabit ilə qoruyuruq.
const WS_OPEN = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;

let singleton: PosWsClient | null = null;

/** Prosesdə tək pos_local WS klienti. */
export function getPosWs(): PosWsClient {
  if (!singleton) singleton = new PosWsClient();
  return singleton;
}

export type { PosWsClient };
